import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Briefcase, Loader2, AlertTriangle, CheckCircle, Sparkles } from "lucide-react";
import { toast } from "sonner";
import BrandedHeader from "@/components/BrandedHeader";
import QuestionNav from "@/components/take-test/QuestionNav";
import QuestionPane from "@/components/take-test/QuestionPane";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * Public, no-auth "Practice Mode" page.
 * Reuses QuestionPane + QuestionNav. Scoring is purely client-side; no
 * candidate record is created. Questions are sampled from published tests
 * matching the requested category via GET /api/public/practice/:category.
 */
export default function PracticeModePage() {
  const { category } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [currentQ, setCurrentQ] = useState(0);
  const [scored, setScored] = useState(null);

  const loadQuestions = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND}/api/public/practice/${encodeURIComponent(category)}`);
      setQuestions(res.data.questions || []);
    } catch (e) {
      const detail = e.response?.data?.detail || "Unable to load practice questions";
      setError(typeof detail === "string" ? detail : "Unable to load practice questions");
    } finally {
      setLoading(false);
    }
  }, [category]);

  useEffect(() => {
    loadQuestions();
  }, [loadQuestions]);

  const handleSelect = (qIdx, option) => setAnswers({ ...answers, [String(qIdx)]: option });

  const handleSubmit = () => {
    if (Object.keys(answers).length === 0) {
      toast.error("Answer at least one question before submitting");
      return;
    }
    let correct = 0;
    questions.forEach((q, i) => {
      const picked = (answers[String(i)] || "").toUpperCase();
      if (picked === (q.correct_answer || "").toUpperCase()) correct += 1;
    });
    const total = questions.length;
    const pct = Math.round((correct / total) * 100);
    setScored({ correct, total, pct });
    window.scrollTo({ top: 0, behavior: "smooth" });
    toast.success("Practice complete — review your answers below");
  };

  const handleRestart = () => {
    setAnswers({});
    setCurrentQ(0);
    setScored(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-primary animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4" data-testid="practice-error-screen">
        <Card className="max-w-md w-full border-border bg-card">
          <CardContent className="p-8 text-center">
            <AlertTriangle className="w-12 h-12 text-[hsl(var(--warning))] mx-auto mb-4" />
            <h2 className="text-xl font-display font-semibold mb-2">Practice unavailable</h2>
            <p className="text-muted-foreground mb-4">{error}</p>
            <Button variant="outline" onClick={() => navigate("/")} data-testid="practice-error-back-btn">
              Back to home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const totalQ = questions.length;
  const answered = Object.keys(answers).length;
  const currentQuestion = questions[currentQ];

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="practice-mode-page">
      <BrandedHeader className="flex-shrink-0">
        <div className="flex items-center gap-3 flex-1">
          <Sparkles className="w-5 h-5 text-primary" />
          <div>
            <span className="font-display font-semibold text-sm">Practice Mode</span>
            <span className="text-xs text-muted-foreground ml-2">
              Category: <strong className="capitalize">{category}</strong>
            </span>
          </div>
          <div className="flex items-center gap-3 ml-auto">
            <Badge variant="secondary" className="text-xs">
              <Briefcase className="w-3 h-3 mr-1" /> No record kept
            </Badge>
            <span className="text-xs text-muted-foreground">{answered}/{totalQ}</span>
          </div>
        </div>
      </BrandedHeader>

      <div className="flex-1 px-4 sm:px-6 py-6 overflow-auto">
        <div className="max-w-3xl mx-auto">
          {scored ? (
            <Card className="border-border bg-card mb-6" data-testid="practice-result-card">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 rounded-full bg-[hsl(var(--success))]/10 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-[hsl(var(--success))]" />
                </div>
                <h2 className="text-2xl font-display font-semibold mb-1">Practice complete</h2>
                <p className="text-4xl font-display font-semibold mt-4 mb-1">{scored.pct}%</p>
                <p className="text-muted-foreground">{scored.correct}/{scored.total} correct</p>
                <div className="bg-secondary/50 rounded-lg p-4 mt-6 text-left">
                  <p className="text-sm text-muted-foreground">
                    Review each question below — correct answers are highlighted. Ready for the real thing?
                    Head to <strong>Careers</strong> and apply.
                  </p>
                </div>
                <div className="flex justify-center gap-3 mt-6">
                  <Button variant="outline" onClick={handleRestart} data-testid="practice-restart-btn">
                    Try again
                  </Button>
                  <Button onClick={() => navigate("/candidate/jobs")} data-testid="practice-browse-jobs-btn">
                    Browse open roles
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <>
              <div className="mb-6">
                <div className="flex justify-between text-xs text-muted-foreground mb-2">
                  <span>Question {currentQ + 1} of {totalQ}</span>
                  <span>{Math.round((answered / totalQ) * 100)}% complete</span>
                </div>
                <Progress value={(answered / totalQ) * 100} className="h-1.5" />
              </div>

              <QuestionNav questions={questions} currentQ={currentQ} answers={answers} onSelect={setCurrentQ} />

              <QuestionPane
                question={currentQuestion}
                index={currentQ}
                totalQ={totalQ}
                answers={answers}
                onSelect={handleSelect}
                onPrev={() => setCurrentQ(Math.max(0, currentQ - 1))}
                onNext={() => setCurrentQ(currentQ + 1)}
                onSubmit={handleSubmit}
                submitting={false}
              />
            </>
          )}

          {scored && (
            <div className="space-y-3" data-testid="practice-review-list">
              {questions.map((q, idx) => {
                const picked = answers[String(idx)];
                const isCorrect = (picked || "").toUpperCase() === (q.correct_answer || "").toUpperCase();
                return (
                  <Card key={`practice-review-${idx}`} className="border-border bg-card">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <h4 className="text-sm font-medium">Q{idx + 1}. {q.question_text}</h4>
                        <Badge variant={isCorrect ? "default" : "destructive"} className="text-xs" data-testid={`practice-review-badge-${idx}`}>
                          {isCorrect ? "Correct" : "Review"}
                        </Badge>
                      </div>
                      <div className="space-y-1 text-sm">
                        {q.options.map((opt) => {
                          const isAnswer = opt.label === q.correct_answer;
                          const isPicked = picked === opt.label;
                          let rowCls = "text-muted-foreground";
                          if (isAnswer) rowCls = "text-[hsl(var(--success))] font-medium";
                          else if (isPicked) rowCls = "text-[hsl(var(--destructive))]";
                          return (
                            <div key={`review-${idx}-${opt.label}`} className={`rounded px-2 py-1 ${isAnswer ? "bg-[hsl(var(--success))]/10" : ""} ${rowCls}`}>
                              <span className="font-semibold mr-2">{opt.label}.</span>{opt.text}
                              {isPicked && !isAnswer && <span className="ml-2 text-xs">(your pick)</span>}
                            </div>
                          );
                        })}
                      </div>
                      {q.explanation && (
                        <p className="text-xs text-muted-foreground mt-2 italic">💡 {q.explanation}</p>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
